import { Button } from '@/components/Button';
import {
  CardBiography,
  CardFooter,
  CardHeader,
  CardLinks,
  CardNotFound,
  CardProfile,
} from '@/components/DigitalCard';
import { Loading } from '@/components/Loading';
import { Modal } from '@/components/Modal';
import { PremiumFeatureOverlay } from '@/components/PremiumFeatureOverlay';
import { useDirectLink } from '@/hooks/useDirectLink';
import { useShareProfile } from '@/hooks/useShareProfile';
import { useVCardGenerator } from '@/hooks/useVCardGenerator';
import { ContactForm } from '@/partials/dashboard/Contacts/ContactForm';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import useContactStore from '@/stores/useContactStore';
import { CreateContact as CreateContactType } from '@/types/contact';
import { handleError } from '@/utils/handleError';
import { FormikHelpers } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const useCardData = (slug: string | undefined) => {
  const { card, loading, showPublicCard } = useCardStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCard = async () => {
      if (!slug) return;

      try {
        await showPublicCard(slug);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('errors.card-not-found'),
        );
      }
    };

    loadCard();
  }, [slug, showPublicCard]);

  return { card, loading, error };
};

const ErrorView = ({ message }: { message: string }) => (
  <div className="flex min-h-screen flex-col items-center justify-center">
    <CardNotFound />
    <p className="mt-4 text-gray-600">{message}</p>
  </div>
);

export const DigitalCard = () => {
  const { slug } = useParams<{ slug: string }>();
  const { card, loading, error } = useCardData(slug);
  const { generateVCard } = useVCardGenerator(card);
  const { shareProfile } = useShareProfile(card);
  const { user } = useAuthStore();
  const [isBlurred, setIsBlurred] = useState(false);
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { createContact, loading: contactLoading } = useContactStore();
  const [openSource, setOpenSource] = useState<'lead_capture' | 'exchange'>(
    'exchange',
  );

  // Vérifier si les limites de visites sont dépassées
  useEffect(() => {
    if (!card) return;

    if (card.visits_exceeded) {
      setIsBlurred(true);
    } else {
      setIsBlurred(false);
    }
  }, [card]);

  // Vérifier si lead capture est activé et ouvrir le modal automatiquement
  useEffect(() => {
    if (!card) return;

    if (card.lead_capture && card.lead_capture.is_active && !formSubmitted) {
      setIsExchangeModalOpen(true);
      setOpenSource('lead_capture');
    }
  }, [card, formSubmitted]);

  // Fermer le modal lorsque le formulaire est soumis avec succès
  useEffect(() => {
    if (formSubmitted) {
      setIsExchangeModalOpen(false);
    }
  }, [formSubmitted]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const directLink = useMemo(
    () => card?.links?.find(link => link.is_direct),
    [card?.links],
  );
  const { hasRedirected } = useDirectLink(directLink);

  const filteredLinks = useMemo(
    () => card?.links?.filter(link => link.is_active) || null,
    [card?.links],
  );

  const handleShare = useCallback(() => {
    shareProfile();
  }, [shareProfile]);

  const handleGenerateVCard = useCallback(() => {
    generateVCard();
  }, [generateVCard]);

  const handleExchange = useCallback(() => {
    setOpenSource('exchange');
    setIsExchangeModalOpen(true);
  }, []);

  const closeExchangeModal = useCallback(() => {
    // Si lead capture est activé et que le formulaire n'a pas encore été soumis,
    // empêcher la fermeture du modal
    if (
      card?.lead_capture?.is_active &&
      !formSubmitted &&
      openSource === 'lead_capture'
    ) {
      return;
    }
    setIsExchangeModalOpen(false);
  }, [card, formSubmitted, openSource]);

  const initialContactValues: CreateContactType = useMemo(
    () => ({
      full_name: '',
      email: '',
      phone: '',
      company: '',
      notes: card
        ? t('digitalCard:exchange.defaultNote', { name: card.full_name })
        : '',
      source: openSource,
      user_id: card?.user_id || 0,
    }),
    [card, openSource],
  );

  const handleCreateContact = async (
    values: CreateContactType,
    { setErrors, setSubmitting }: FormikHelpers<CreateContactType>,
  ) => {
    if (!card?.user_id) return;

    setFormSubmitting(true);
    setSubmitting(true);
    try {
      const contact = await createContact(card.user_id, values, card.id);
      if (contact) {
        setFormSubmitted(true);
        toast.success(t('digitalCard:exchange.success'));
        closeExchangeModal();
      }
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setFormSubmitting(false);
      setSubmitting(false);
    }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorView message={error} />;
  if (hasRedirected) return null;
  if (!card) return <CardNotFound />;

  // Personnaliser le texte du modal en fonction de lead_capture s'il est activé
  const modalTitle = card.lead_capture?.is_active
    ? card.lead_capture.title || t('digitalCard:exchange.title')
    : t('digitalCard:exchange.title');

  const modalDescription = card.lead_capture?.is_active
    ? card.lead_capture.description ||
      t('digitalCard:exchange.description', { name: card.full_name })
    : t('digitalCard:exchange.description', { name: card.full_name });

  const buttonText = card.lead_capture?.is_active
    ? card.lead_capture.button_text || t('digitalCard:exchange.submit')
    : t('digitalCard:exchange.submit');

  const disclaimer = card.lead_capture?.is_active
    ? card.lead_capture.disclaimer
    : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="relative flex min-h-screen w-full items-center justify-center bg-gray-100"
      >
        {isBlurred ? (
          <PremiumFeatureOverlay
            showUpgradeButton={!!user && card?.user_id === user.id}
            title={t('digitalCard:visit-limit-exceeded')}
            message={t('digitalCard:upgrade-subscription')}
            className="md:rounded-2xl"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="relative flex min-h-screen w-full flex-col justify-between overflow-y-auto shadow-lg md:w-[400px] md:rounded-2xl lg:w-[480px]"
              style={{ backgroundColor: card.color }}
            >
              <div className="relative">
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <CardHeader
                    coverImage={card.cover}
                    avatarImage={card.avatar}
                    onShare={handleShare}
                    cardColor={card.color}
                  />
                </motion.div>

                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <CardProfile
                    fullName={card.full_name}
                    job={card.job}
                    company={card.company}
                    onAddContact={handleGenerateVCard}
                    onExchange={handleExchange}
                  />
                </motion.div>

                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="px-6"
                >
                  <div className="space-y-8 py-8">
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6 }}
                    >
                      <CardBiography biography={card.biography} />
                    </motion.div>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <CardLinks links={filteredLinks} />
                    </motion.div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <CardFooter year={currentYear} />
              </motion.div>
            </motion.div>
          </PremiumFeatureOverlay>
        ) : (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="relative flex min-h-screen w-full flex-col justify-between overflow-y-auto shadow-lg md:w-[400px] md:rounded-2xl lg:w-[480px]"
            style={{ backgroundColor: card.color }}
          >
            <div className="relative">
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <CardHeader
                  coverImage={card.cover}
                  avatarImage={card.avatar}
                  onShare={handleShare}
                  cardColor={card.color}
                />
              </motion.div>

              <motion.div
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <CardProfile
                  fullName={card.full_name}
                  job={card.job}
                  company={card.company}
                  onAddContact={handleGenerateVCard}
                  onExchange={handleExchange}
                />
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="px-6"
              >
                <div className="space-y-8 py-8">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    <CardBiography biography={card.biography} />
                  </motion.div>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    <CardLinks links={filteredLinks} />
                  </motion.div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <CardFooter year={currentYear} />
            </motion.div>
          </motion.div>
        )}

        {/* Modal d'échange de contacts */}
        <Modal
          isOpen={isExchangeModalOpen}
          onClose={closeExchangeModal}
          title={modalTitle}
          hideCloseButton={card.lead_capture?.is_active && !formSubmitted}
        >
          <p className="mb-4 text-gray-600">{modalDescription}</p>
          <ContactForm
            initialValues={initialContactValues}
            onSubmit={handleCreateContact}
            isLoading={contactLoading || formSubmitting}
            translationPrefix="digitalCard:exchange"
            footer={
              <Button
                type="submit"
                disabled={contactLoading || formSubmitting}
                className="col-span-2 w-full rounded-full bg-linko-light"
              >
                {buttonText}
              </Button>
            }
          />
          {disclaimer && disclaimer.length > 0 && (
            <p className="mt-4 text-xs text-gray-500">{disclaimer}</p>
          )}
        </Modal>
      </motion.div>
    </AnimatePresence>
  );
};
